import { httpApi } from '@api/http.api.ts';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import type { CommonTableRequest, CreatorRevision } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { ELEMENTS_STATUSES } from '@routes/sidebar/admin-center/creator/___pages___/elements/ui/tables/elements-table/constants.ts';
import type {
    CreatorContent,
    CreatorPathway,
} from '@routes/sidebar/admin-center/creator/___pages___/pathways/api/creatorPathways.api.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type QuizQuestionsCount = {
    totalNumberOfQuestions: number;
    questionsToSelect: number;
};

export type ElementStatusesType = (typeof ELEMENTS_STATUSES)[keyof typeof ELEMENTS_STATUSES];

// ItemQuizQuestion --> request/create/update shape, no questionId required
// ItemQuizQuestionWithQuestionId --> strict API response shape
// ItemQuizQuestionWithOptionalQuestionId --> local/UI mixed shape
// ItemQuizQuestionWithLocalId  --> local/UI mixed shape + temporaryLocalId

export type Subtask = {
    description: string;
    guideline: string;
};

type ElementTask = {
    taskScaleType: TaskScaleType;
    subTasks: Subtask[];
    referenceText?: string | null;
};

export interface CreatorElement {
    id: number;
    elementId: string | null;
    currentPublishedElementId: number | null;
    title: string;
    type: string;
    durationInSeconds: number | null;
    creatorReference: string | null;
    createdBy: string | null;
    createdFor: string | null;
    createdByDisplayName?: string | null;
    createdForDisplayName?: string | null;
    learningObjective: string | null;
    status: ElementStatusesType;
    version: string;
    elementOrder: number; // pathways check
    isPracticeQuizEnabled: boolean; // this will allow users to enable practice quiz
    contentFileSize: number | null;
    contentFileName: string | null;
    videoURI: string | null;
    thumbnailURI: string | null;
    documentURI: string | null;
    contentId: string | null;
    thumbnailId: string | null;
    thumbnailContent?: CreatorContent | null;
    text: string | null;
    practiceQuizQuestions: QuizQuestionsCount;
    itemQuizQuestions: ItemQuizQuestionWithOptionalQuestionId[];
    finalQuizQuestions: QuizQuestionsCount;
    timeInSecondsPerQuestion: number;
    totalUsedInPathways: number;
    createdAtUTC: string | null;
    updatedAtUTC: string | null;
    disableSeekingOnFirstPlay: boolean;
    elementTaskConfiguration: ElementTask | null;
    isContentReady: boolean;
    scorePercentThreshold?: number | null;
    content?: CreatorContent | null;
    creator?: string | null;
    adminPathwayItemId?: string | null;
    isQuizItem?: boolean;
}

export interface CreatorElementDetailsResponse {
    id: number;
    revisions: CreatorRevision[];
    elementAdminInfo: CreatorElement;
    usedInPathways: CreatorPathway[];
    itemQuizQuestions: ItemQuizQuestionWithQuestionId[];
}

export interface PostElementRequest {
    title: string;
    creator: string;
    learningObjective: string;
    createdFor?: string | null;
    type?: string;
    durationInSeconds?: number;
    creatorReference?: string | null;
    contentId?: string | null;
    thumbnailId?: string | null;
    videoURI?: string | null;
    itemQuizQuestions?: ItemQuizQuestion[];
    timeInSecondsPerQuestion: number;
    practiceQuizQuestions: QuizQuestionsCount;
    finalQuizQuestions: QuizQuestionsCount;
    status?: ElementStatusesType;
    revisionNotes?: string;
    creatorComments?: string;
    elementTaskConfiguration?: ElementTask;
}

export interface PutElementRequest {
    title?: string;
    creator?: string;
    createdFor?: string | null;
    type?: string;
    durationInSeconds?: number;
    creatorReference?: string | null;
    contentId?: string | null;
    thumbnailId?: string | null;
    videoURI?: string | null;
    status?: ElementStatusesType;
    comment?: string;
    learningObjective?: string | null;
    revisionNotes?: string;
    creatorComments?: string;
    itemQuizQuestions?: ItemQuizQuestion[];
    timeInSecondsPerQuestion: number;
    practiceQuizQuestions: QuizQuestionsCount;
    finalQuizQuestions: QuizQuestionsCount;
    elementTaskConfiguration?: ElementTask;
}

export interface ItemQuizQuestionWithOptionalQuestionId extends ItemQuizQuestion {
    questionId?: string | null;
}

export interface ItemQuizQuestionWithQuestionId extends ItemQuizQuestion {
    questionId: string | null;
}

export interface ItemQuizQuestion {
    id: number | null;
    adminQuestionId: string | null;
    quizAnswers: Answer[];
    quizQuestionVariations: Variation[];
    introText: string;
    outroText?: string | null;

    isFinalQuizQuestion: boolean;
    totalAnswersToSelect: number;
    minimumCorrectAnswersToSelect?: number;
    requiredAnswerType: string;

    correctFeedback?: string | null;
    partiallyCorrectFeedback?: string | null;
    wrongFeedback?: string | null;
    status: Omit<ElementStatusesType, 'Archive'>;
    isActive: boolean;
    isDeleted?: boolean;
}

export interface Answer {
    isCorrect: boolean;
    text: string;
    adminAnswerId: string | null;
    isDeleted?: boolean;
    points?: number;
}

export interface Variation {
    quizContent: string;
    quizContentType: string;
}

export type CommonCreatorElementsRequest = CommonTableRequest;

interface GetCreatorElementsParams {
    payload: CommonCreatorElementsRequest;
}

interface GetCreatorElementByIdParams {
    id: string | number;
}

interface UpdateCreatorElementParams {
    id: string | number;
    payload: PutElementRequest;
}

interface CreateCreatorElementParams {
    payload: PostElementRequest;
}

interface DeleteCreatorElementById extends GetCreatorElementByIdParams {
    comment: string | null;
}

const BASE_URL = 'admin-center/elements';

export const getApiCreatorElements = ({
    payload,
}: GetCreatorElementsParams): Promise<PaginatedResponse<CreatorElement, EmptyResultsDescription>> =>
    httpApi
        .post<PaginatedResponse<CreatorElement, EmptyResultsDescription>>(`${BASE_URL}/search`, payload)
        .then(({ data }) => data);

export const getApiCreatorElementById = ({ id }: GetCreatorElementByIdParams): Promise<CreatorElementDetailsResponse> =>
    httpApi.get<CreatorElementDetailsResponse>(`${BASE_URL}/${id}`).then(({ data }) => data);

export const createApiCreatorElement = ({
    payload,
}: CreateCreatorElementParams): Promise<CreatorElementDetailsResponse> =>
    httpApi.post<CreatorElementDetailsResponse>(`${BASE_URL}`, payload).then(({ data }) => data);

export const updateApiCreatorElement = ({
    id,
    payload,
}: UpdateCreatorElementParams): Promise<CreatorElementDetailsResponse> =>
    httpApi.put<CreatorElementDetailsResponse>(`${BASE_URL}/${id}`, payload).then(({ data }) => data);

export const deleteApiCreatorElement = ({ id, comment }: DeleteCreatorElementById): Promise<any> =>
    httpApi.delete(`${BASE_URL}/${id}`, { data: { comment } }).then(({ data }) => data);
